import { Controller } from "@hotwired/stimulus"
import { stalePromptHtml, selectedSummaryHtml } from "./public_accommodation/templates"

/**
 * Unified accommodation controller for the public IBE.
 *
 * Modes:
 *   off       — no dive item requires accom; section hidden
 *   optional  — modal-based ad-hoc accom; "Add Accommodation" CTA
 *   bundled   — inline package accom; picker rendered, hotel mandatory
 *
 * Canonical state lives in hidden form inputs at the form root:
 *   booking[requires_accommodation]  "0" | "1"
 *   booking[check_in_date]           "YYYY-MM-DD"
 *   booking[check_out_date]          "YYYY-MM-DD"
 *   booking[room_type]               slug
 *   booking[pms_room_type_id]        string
 *   booking[guests_count]            int
 *   booking[rooms_count]             int
 *   booking[availability_snapshot]   JSON
 *
 * In-memory fields are a derived view; connect() rebuilds them from the
 * inputs so server re-renders and deep-link prefill share one rehydration
 * path with a fresh mount.
 *
 * Cross-controller wiring: listens for public-dives:datesChanged and
 * public-dives:selectionChanged via data-action declared on the form.
 * Wizard's submit guard calls canSubmit() via Stimulus outlet.
 */
export default class extends Controller {
  static targets = [
    "section", "inlinePicker", "modalTrigger",
    "summary", "datePicker", "results", "pricing", "loading",
    "dateWarning", "dateWarningText",
    "accommodationModal", "accomDatePicker"
  ]

  static outlets = ["accom-date-picker"]

  static values = {
    mode: { type: String, default: "off" },
    packageConfig: { type: Object, default: {} },
    packageConfigs: { type: Array, default: [] },
    availabilityUrl: { type: String, default: "/availability" },
    locales: { type: Object, default: {} }
  }

  connect() {
    this._rehydrateFromInputs()
    this.render()
  }

  // ===== Event handlers (wired via data-action) =====

  onDivesChanged(event) {
    this._recomputeCoverage(event.detail.diveDates || [])
  }

  onSelectionChanged(event) {
    this._deriveMode(event.detail.selectionSummary || [])
    this.render()
  }

  // ===== Submit guard (called by wizard via outlet) =====

  canSubmit() {
    if (this.modeValue === "off") return { ok: true }
    if (this._requiresAccommodation() && this._coverage() === "stale") {
      return { ok: false, reason: "stale", focusElement: this.sectionTarget }
    }
    if (this._requiresAccommodation() && !this._roomPicked()) {
      return { ok: false, reason: "needs-pick", focusElement: this.sectionTarget }
    }
    return { ok: true }
  }

  // Public contract for the wizard's outlet. Returns the accommodation
  // invoice lines + total in cents, or null when no accommodation is selected.
  // The wizard appends these to the Payment-step invoice display so the
  // total includes accommodation alongside dives.
  getInvoiceLines() {
    if (!this._requiresAccommodation() || !this._roomPicked()) return null

    // Bundled mode: package-rate × packageNights + standard-rate × extraNights
    if (this.modeValue === "bundled" && this._selectedRoom?.pkgRateInclTax != null) {
      const nights = this.getNights()
      const config = this.packageConfigValue || {}
      const pkgDuration = config.duration_days || nights
      const packageNights = Math.min(nights, pkgDuration)
      const extraNights = Math.max(0, nights - pkgDuration)
      const pkgRate = parseFloat(this._selectedRoom.pkgRateInclTax) || 0
      const stdRate = parseFloat(this._selectedRoom.stdRateInclTax) || 0
      const roomName = this._selectedRoom.roomName || this._roomType || "Room"

      const lines = []
      if (packageNights > 0) {
        lines.push({
          label: roomName,
          qtyLabel: `×${packageNights} nights (package)`,
          amount_cents: Math.round(packageNights * pkgRate * 100)
        })
      }
      if (extraNights > 0) {
        lines.push({
          label: roomName,
          qtyLabel: `×${extraNights} nights (standard)`,
          amount_cents: Math.round(extraNights * stdRate * 100)
        })
      }
      const totalCents = lines.reduce((sum, l) => sum + l.amount_cents, 0)
      return { lines, totalCents }
    }

    // Optional / modal-stash fallback: when we have a per-night inclusive
    // rate, use it. nights mirrors selectedSummaryHtml exactly —
    // `_modalNights || getNights()` — so the line on the Review/Payment
    // step matches the price the customer already sees in the green
    // summary card. The `||` (not `??`) is deliberate: when the modal
    // shipped data-nights="0" because the picker hadn't received dates
    // yet, `_modalNights` ends up 0; falling through to getNights() keeps
    // the total from collapsing to $0 while the summary card shows the
    // real figure.
    if (this._modalRateCents != null) {
      const rooms = this._rooms || 1
      const nights = this._modalNights || this.getNights()
      if (nights > 0) {
        const rateCents = this._modalRateCents
        const totalCents = nights * rooms * rateCents
        return {
          lines: [{
            label: this._roomType || "Room",
            qtyLabel: `×${nights} nights${rooms > 1 ? `, ${rooms} rooms` : ""}`,
            amount_cents: totalCents
          }],
          totalCents
        }
      }
    }

    // Rehydrated state without rate info — can't price; return nothing rather
    // than rendering $0 (the customer saw the full price on Review).
    return null
  }

  // Public contract for the wizard's Payment step: itemize the inclusive
  // accommodation total into Room (ex-tax) + Service Charge + T-GST + Green
  // Tax so the customer sees the same breakdown before clicking Pay that they
  // get on the confirmation page. Returns null when we don't have the
  // ingredients (no tax_config yet, no ex-tax base, no selection) so callers
  // can degrade gracefully to the inclusive-only display.
  getTaxBreakdown() {
    if (!this._requiresAccommodation() || !this._roomPicked()) return null
    if (!this._taxConfig) return null

    const guests = this._guests || 2
    const rooms = this._rooms || 1

    // Modal flow stash: per-night ex-tax base captured at confirmRoom time
    // and per-night inclusive sits in _modalRateCents.
    if (this._selectedRoom?.rateExTaxCents > 0 && this._modalNights != null) {
      return this._taxBreakdownFromBase(
        this._selectedRoom.rateExTaxCents,
        guests, this._modalNights, rooms
      )
    }

    // Bundled mode: rate lives on _selectedRoom.rateExTax (USD float),
    // nights from the canonical input range.
    if (this._selectedRoom?.rateExTax != null && this.modeValue === "bundled") {
      const nights = this.getNights()
      const baseCents = Math.round(parseFloat(this._selectedRoom.rateExTax) * 100)
      return this._taxBreakdownFromBase(baseCents, guests, nights, rooms)
    }

    return null
  }

  _taxBreakdownFromBase(baseExTaxPerNightCents, adults, nights, rooms) {
    const bd = this._inclusiveCascadeCents(baseExTaxPerNightCents, adults, nights)
    const cfg = this._taxConfig || {}
    return {
      baseCents: baseExTaxPerNightCents * nights * rooms,
      scCents: bd.scPerNightCents * nights * rooms,
      tgstCents: bd.tgstPerNightCents * nights * rooms,
      greenCents: bd.greenPerNightCents * nights * rooms,
      taxesTotalCents: (bd.scPerNightCents + bd.tgstPerNightCents + bd.greenPerNightCents) * nights * rooms,
      scPct: cfg.service_charge_rate || 0,
      tgstPct: cfg.tax_rate || 0,
      adults: adults,
      nights: nights,
      rooms: rooms
    }
  }

  // ===== Canonical state plumbing + mode/coverage =====

  _rehydrateFromInputs() {
    if (!this._requiresAccommodation()) return
    const checkIn = this._input("check_in_date")?.value
    const checkOut = this._input("check_out_date")?.value
    if (!checkIn || !checkOut) return

    this._checkIn = checkIn
    this._checkOut = checkOut
    this._roomType = this._input("room_type")?.value
    this._pmsRoomTypeId = this._input("pms_room_type_id")?.value
    this._guests = parseInt(this._input("guests_count")?.value, 10) || 2
    this._rooms = parseInt(this._input("rooms_count")?.value, 10) || 1

    // Restore a minimal _selectedRoom so the summary card renders after a
    // server re-render. We DON'T restore the inline pricing-breakdown rates
    // (rateExTax, pkgRateInclTax, stdRateInclTax) because those came from
    // _availabilityRooms, which isn't part of canonical inputs. The summary
    // card path (_renderSummary -> selectedSummaryHtml) doesn't need rates;
    // the pricing-breakdown card simply won't render, which is the correct
    // fallback (customer sees the summary card instead).
    if (this._roomType || this._pmsRoomTypeId) {
      this._selectedRoom = {
        roomName: this._roomType,
        roomTypeId: this._pmsRoomTypeId
      }
    }
  }
  // Walk the dives controller's selectionSummary payload and decide which
  // mode the accommodation section is in. Sets modeValue and packageConfigValue.
  _deriveMode(selection) {
    const items = selection.flatMap(group => group.items || [])
    if (items.length === 0) {
      this.modeValue = "off"
      this.packageConfigValue = {}
      return
    }

    // Find the first selected product that has a bundled-accom config.
    // packageConfigsValue is the catalog of all bundled-package configs
    // rendered into the controller from the ERB.
    let bundledConfig = null
    for (const item of items) {
      const cfg = this.packageConfigsValue.find(c => c.value === item.value)
      if (cfg && cfg.accommodation_room_type_ids && cfg.accommodation_room_type_ids.length > 0) {
        bundledConfig = cfg
        break
      }
    }

    if (bundledConfig) {
      // If the customer had previously picked a room and the new
      // bundled-package's room-type allowlist no longer includes it,
      // drop accom (same path as stale).
      const previousPmsId = this._pmsRoomTypeId
      const stillAllowed = previousPmsId &&
        bundledConfig.accommodation_room_type_ids.includes(previousPmsId)
      if (previousPmsId && !stillAllowed) this._clearAccommodation()

      this.packageConfigValue = bundledConfig
      this.modeValue = "bundled"
    } else {
      this.modeValue = "optional"
      this.packageConfigValue = {}
    }
  }

  // Called when dive_dates change (event from dives controller).
  //
  // Case A — accommodation is currently selected: if the new dive-date span
  //   falls outside the picked stay, clear the accommodation and surface the
  //   stale-prompt CTA.
  //
  // Case B — stale prompt is already showing (accom was previously cleared):
  //   keep _stalePromptRange in sync with the latest dive-date span so the
  //   CTA text and picker pre-fill always reflect the CURRENT dive selection,
  //   not the span as it was when the stale event first fired.
  _recomputeCoverage(diveDates) {
    if (diveDates.length === 0) return

    const sorted = [...diveDates].sort()
    const earliest = sorted[0]
    const latest = sorted[sorted.length - 1]

    if (this._requiresAccommodation()) {
      // Case A: accommodation is selected — check if the stay now misses any dive dates.
      if (!this._checkIn || !this._checkOut) return
      const stale = earliest < this._checkIn || latest > this._checkOut
      if (stale) {
        this._stalePromptRange = { earliest, latest }
        this._clearAccommodation()
        // _clearAccommodation → _commit resets _stalePromptRange (the writer
        // invariant — see _commit). Re-set it so the prompt renders after the clear.
        this._stalePromptRange = { earliest, latest }
        this.render()
      }
      return
    }

    // Case B: no accommodation selected. If the stale prompt is visible, keep
    // its range up to date so subsequent dive-date edits don't freeze the CTA.
    if (this._stalePromptRange) {
      const prev = this._stalePromptRange
      if (prev.earliest !== earliest || prev.latest !== latest) {
        this._stalePromptRange = { earliest, latest }
        this.render()
      }
    }
  }

  _coverage() {
    if (!this._requiresAccommodation()) return "ok"
    if (!this._roomPicked()) return "needs-pick"
    const dates = this._currentDiveDates()
    if (dates.length === 0) return "ok"
    if (dates[0] < this._checkIn || dates[dates.length - 1] > this._checkOut) return "stale"
    return "ok"
  }

  // Canonical source for dive dates from anywhere in the controller.
  // Reads the form's hidden dive_dates inputs, deduplicates and sorts —
  // every consumer expects the same shape, so centralizing prevents drift.
  _currentDiveDates() {
    const inputs = this.element.querySelectorAll('input[name="booking[dive_dates][]"]')
    return [...new Set(Array.from(inputs).map(i => i.value).filter(Boolean))].sort()
  }

  _requiresAccommodation() { return this._input("requires_accommodation")?.value === "1" }
  _roomPicked() { return !!this._input("pms_room_type_id")?.value }
  // Scoped to this.element (the form) for the same defense-in-depth reason
  // _currentDiveDates is — a rogue same-named input elsewhere on the page
  // shouldn't influence what we read for the canonical accommodation state.
  _input(name) { return this.element.querySelector(`[name="booking[${name}]"]`) }

  // Write all canonical inputs in one place. Pass null/falsy to clear.
  // Invariant: NOTHING else writes the canonical inputs. If a future Turbo
  // Stream / morph operation needs to update them, route through _commit() too.
  // This clears _stalePromptRange so a fresh commit doesn't render the old CTA.
  _commit({ requires, checkIn, checkOut, roomType, pmsRoomTypeId, guests, rooms, snapshot }) {
    this._setInput("requires_accommodation", requires ? "1" : "0")
    this._setInput("check_in_date", checkIn || "")
    this._setInput("check_out_date", checkOut || "")
    this._setInput("room_type", roomType || "")
    this._setInput("pms_room_type_id", pmsRoomTypeId || "")
    if (guests != null) this._setInput("guests_count", String(guests))
    if (rooms != null) this._setInput("rooms_count", String(rooms))
    if (snapshot !== undefined) this._setInput("availability_snapshot", snapshot || "")

    this._checkIn = checkIn || null
    this._checkOut = checkOut || null
    this._roomType = roomType || null
    this._pmsRoomTypeId = pmsRoomTypeId || null
    this._stalePromptRange = null
  }

  _setInput(name, value) {
    const el = this._input(name)
    if (el) el.value = value
  }

  _clearAccommodation() {
    this._commit({
      requires: false,
      checkIn: null, checkOut: null,
      roomType: null, pmsRoomTypeId: null,
      snapshot: null
    })
    // Drop the inline-picker derived view too so showPricingBreakdown's guards
    // hide it on next render. Otherwise the customer sees the "Re-pick" CTA
    // up top + a stale "Beach Room — N nights × $X (package rate)" breakdown
    // below it — two contradictory states for the same logical accommodation.
    this._selectedRoom = null
    this._availabilityRooms = null
    if (this.hasResultsTarget) this.resultsTarget.innerHTML = ""
    if (this.hasPricingTarget) {
      this.pricingTarget.innerHTML = ""
      this.pricingTarget.classList.add("hidden")
    }
    if (this.hasDateWarningTarget) this.dateWarningTarget.classList.add("hidden")
    // Reset the inline bundled-picker's calendar so it doesn't keep
    // highlighting the old stay range after accommodation is dropped.
    // Using the Stimulus outlet rather than getControllerForElementAndIdentifier
    // — the inline datePicker element carries the outlet selector below.
    if (this.hasAccomDatePickerOutlet) {
      this.accomDatePickerOutlet.clear(new Event("synthetic-clear"))
    }
  }

  // ===== Inline package picker (bundled mode) =====

  // Called when date range changes from accom-date-picker
  rangeChanged(event) {
    this._checkIn = event.detail.checkIn
    this._checkOut = event.detail.checkOut
  }

  // Fetch availability filtered to package room types
  async search(event) {
    event.preventDefault()
    if (!this._checkIn || !this._checkOut) return
    if (!this.validateDates()) return

    if (this.hasLoadingTarget) this.loadingTarget.classList.remove("hidden")
    if (this.hasResultsTarget) this.resultsTarget.innerHTML = ""

    const roomTypeIds = this.packageConfigValue?.accommodation_room_type_ids || []
    const url = new URL(this.availabilityUrlValue, window.location.origin)
    url.searchParams.set("check_in", this._checkIn)
    url.searchParams.set("check_out", this._checkOut)
    roomTypeIds.forEach(id => url.searchParams.append("room_type_ids[]", id))

    try {
      const response = await fetch(url, { headers: { "Accept": "application/json" } })
      const data = await response.json()
      if (this.hasLoadingTarget) this.loadingTarget.classList.add("hidden")

      if (data.success && data.rooms && data.rooms.length > 0) {
        this._availabilityRooms = data.rooms
        this.renderRoomCards(data.rooms)
      } else {
        this._availabilityRooms = null
        if (this.hasResultsTarget) {
          this.resultsTarget.innerHTML = '<p class="small text-center py-4" style="color: rgba(255,255,255,0.5);">No rooms available for these dates.</p>'
        }
      }
    } catch (error) {
      if (this.hasLoadingTarget) this.loadingTarget.classList.add("hidden")
      if (this.hasResultsTarget) {
        this.resultsTarget.innerHTML = '<p class="small text-center py-4" style="color: rgba(255,255,255,0.5);">Unable to check availability. Submit your booking and we\'ll confirm manually.</p>'
      }
    }
  }

  renderRoomCards(rooms) {
    if (!this.hasResultsTarget) return

    const nights = this.getNights()
    const config = this.packageConfigValue || {}
    const pkgDuration = config.duration_days || nights
    const extraNights = Math.max(0, nights - pkgDuration)

    const selectedRoomTypeId = this._selectedRoom?.roomTypeId || ""

    this.resultsTarget.innerHTML = rooms.map(room => {
      const { rateExTax, pkgRateInclTax, stdRateInclTax } = this._computeRoomRates(room, config, nights)
      const available = parseInt(room.available, 10) || 0
      const soldOut = available <= 0
      const selectedAttr = (!soldOut && room.room_type_id && room.room_type_id === selectedRoomTypeId)
        ? "data-selected"
        : ""

      return `
        <div class="bw-room-card rounded-lg mb-2 transition-all ${soldOut ? "opacity-50" : ""}"
             style="background: rgba(255,255,255,0.04); border: 1px solid rgba(255,255,255,0.1); ${soldOut ? "" : "cursor: pointer;"}"
             ${soldOut ? "" : 'data-action="click->public-accommodation#selectRoom"'}
             ${selectedAttr}
             data-room-name="${this._escape(room.room_type_name || "Room")}"
             data-room-type-id="${room.room_type_id || ""}"
             data-rate-ex-tax="${rateExTax}"
             data-pkg-rate-incl-tax="${pkgRateInclTax}"
             data-std-rate-incl-tax="${stdRateInclTax}"
             data-available="${available}"
             data-max-adults="${room.max_adults || 2}">
          ${this._roomImageHtml(room)}
          <div class="p-3">
            <div class="d-flex align-items-center justify-content-between">
              <div>
                <div class="fw-medium text-white">${this._escape(room.room_type_name || "Room")}</div>
                <div class="small" style="color: rgba(255,255,255,0.5);">${soldOut ? "Sold out" : available + " available"}</div>
              </div>
              <div class="text-end">
                <div class="fw-bold" style="color: var(--accent-color);">$${pkgRateInclTax}/night</div>
                <div class="small" style="color: rgba(255,255,255,0.4);">package rate incl. tax</div>
                ${extraNights > 0 ? `<div class="small" style="color: rgba(255,255,255,0.4);">+ $${stdRateInclTax}/night standard</div>` : ""}
              </div>
            </div>
          </div>
        </div>`
    }).join("")
  }

  selectRoom(event) {
    const card = event.currentTarget
    if (!card.dataset.roomName) return

    this._selectedRoom = {
      roomName: card.dataset.roomName,
      roomTypeId: card.dataset.roomTypeId,
      rateExTax: parseFloat(card.dataset.rateExTax) || 0,
      pkgRateInclTax: card.dataset.pkgRateInclTax,
      stdRateInclTax: card.dataset.stdRateInclTax,
      available: parseInt(card.dataset.available, 10) || 0,
      maxAdults: parseInt(card.dataset.maxAdults, 10) || 2
    }

    this._markSelectedCard(this.resultsTarget, card)

    const slug = this._selectedRoom.roomName.toLowerCase().replace(/[^a-z0-9]+/g, "-")

    // ONE write to canonical inputs — replaces the old roomTypeTarget /
    // pmsRoomTypeIdTarget direct writes and the requiresAccomTarget=1 in
    // renderRoomCards (which we remove).
    this._commit({
      requires: true,
      checkIn: this._checkIn,
      checkOut: this._checkOut,
      roomType: slug,
      pmsRoomTypeId: this._selectedRoom.roomTypeId,
      guests: this._guests || 2,
      rooms: this._rooms || 1,
      snapshot: this._buildSnapshotJson()
    })

    this.showPricingBreakdown()
    this.render()
  }

  showPricingBreakdown() {
    if (!this.hasPricingTarget || !this._selectedRoom) return

    const nights = this.getNights()
    const config = this.packageConfigValue || {}
    const pkgDuration = config.duration_days || nights
    const packageNights = Math.min(nights, pkgDuration)
    const extraNights = Math.max(0, nights - pkgDuration)

    // Re-derive package/standard rates from the canonical inputs (rate +
    // config) so the breakdown and the card view stay in lockstep. Falls
    // back to the cached pkg/std values on _selectedRoom when rate isn't
    // available (e.g., post-rehydration where only summary fields exist).
    let pkgRate = this._selectedRoom.pkgRateInclTax
    let stdRate = this._selectedRoom.stdRateInclTax
    if (this._selectedRoom.rateExTax) {
      const rates = this._computeRoomRates({ rate: this._selectedRoom.rateExTax }, config, nights)
      pkgRate = rates.pkgRateInclTax
      stdRate = rates.stdRateInclTax
    }
    if (pkgRate == null || stdRate == null) return
    const roomName = this._selectedRoom.roomName

    let html = '<div class="small text-white">'
    html += `<div class="d-flex justify-content-between mb-1">
      <span>${this._escape(roomName)} — ${packageNights} nights × $${pkgRate} (package rate)</span>
      <span class="fw-bold">$${(packageNights * parseFloat(pkgRate)).toFixed(2)}</span>
    </div>`

    if (extraNights > 0) {
      html += `<div class="d-flex justify-content-between mb-1">
        <span>${this._escape(roomName)} — ${extraNights} nights × $${stdRate} (standard rate)</span>
        <span class="fw-bold">$${(extraNights * parseFloat(stdRate)).toFixed(2)}</span>
      </div>`
    }

    const total = packageNights * parseFloat(pkgRate) + extraNights * parseFloat(stdRate)
    html += `<div class="d-flex justify-content-between pt-2 mt-2" style="border-top: 1px solid rgba(255,255,255,0.1);">
      <span class="fw-bold">Total accommodation</span>
      <span class="fw-bold" style="color: var(--accent-color);">$${total.toFixed(2)}</span>
    </div></div>`

    this.pricingTarget.innerHTML = html
    this.pricingTarget.classList.remove("hidden")
  }

  validateDates() {
    if (this.hasDateWarningTarget) this.dateWarningTarget.classList.add("hidden")

    const nights = this.getNights()
    const diveDates = this._currentDiveDates()

    if (diveDates.length > 0) {
      const hasOverlap = diveDates.some(d => d >= this._checkIn && d < this._checkOut)
      if (!hasOverlap) {
        this.showWarning("Your accommodation dates don't overlap with any dive dates.")
        return false
      }
    }

    // Warn if stay is shorter than package duration
    const pkgDuration = this.packageConfigValue?.duration_days || 0
    if (pkgDuration > 0 && nights < pkgDuration) {
      this.showWarning(`Your stay (${nights} nights) is shorter than the package duration (${pkgDuration} days). Package rate still applies.`)
    }

    return true
  }

  showWarning(message) {
    if (this.hasDateWarningTarget && this.hasDateWarningTextTarget) {
      this.dateWarningTextTarget.textContent = message
      this.dateWarningTarget.classList.remove("hidden")
    }
  }

  _buildSnapshotJson() {
    return this._snapshotJson(this._availabilityRooms, this._checkIn, this._checkOut, true)
  }

  // Serializes an /availability response into the admin-note JSON that
  // ships with the booking. `includePackageContext: true` adds the package
  // discount/tax fields used by the bundled-mode invoice generator.
  _snapshotJson(rooms, checkIn, checkOut, includePackageContext) {
    if (!rooms) return null
    const snapshot = {
      timestamp: new Date().toISOString(),
      check_in: checkIn,
      check_out: checkOut,
      rooms: rooms.map(r => ({
        room_type_id: r.room_type_id || "",
        room_type_name: r.room_type_name || r.room_name || "Room",
        available: r.available,
        rate: r.rate,
        rate_ex_tax_per_night_cents: r.rate_ex_tax_per_night_cents,
        currency: r.currency || "USD"
      }))
    }
    if (includePackageContext) {
      snapshot.package_discount_cents = this.packageConfigValue?.pms_discount_cents || 0
      snapshot.package_tax_pct = this.packageConfigValue?.pms_tax_percentage || 0
    }
    return JSON.stringify(snapshot)
  }

  getNights() {
    return this._nightsBetween(this._checkIn, this._checkOut)
  }

  // Shared rate math used by both renderRoomCards and showPricingBreakdown.
  // Reduces drift risk: if the package-discount or tax formula changes, it
  // only changes here. Returns string-formatted (toFixed) values to match
  // the existing data-attribute / display contract.
  _computeRoomRates(room, config, nights) {
    const pkgDuration = config.duration_days || nights
    const discountCents = config.pms_discount_cents || 0
    const taxPct = config.pms_tax_percentage || 0
    const rateExTax = parseFloat(room.rate) || 0
    const pkgRateExTax = Math.max(0, rateExTax - discountCents / 100)
    const pkgRateInclTax = (pkgRateExTax * (1 + taxPct / 100)).toFixed(2)
    const stdRateInclTax = (rateExTax * (1 + taxPct / 100)).toFixed(2)
    const packageNights = Math.min(nights, pkgDuration)
    const extraNights = Math.max(0, nights - pkgDuration)
    return { rateExTax, pkgRateInclTax, stdRateInclTax, packageNights, extraNights }
  }

  _escape(str) {
    const div = document.createElement("div")
    div.textContent = str
    return div.innerHTML
  }

  // Room card banner: the eZee RoomList `room_main_image` (surfaced as
  // image_url). Falls back to a gradient + icon placeholder when blank, and the
  // <img> onerror swaps to that same placeholder if the URL is missing/broken.
  _roomImageHtml(room) {
    if (!room.image_url) {
      return `<div class="bw-room-image bw-room-image--placeholder"><i class="bi bi-image" aria-hidden="true"></i></div>`
    }
    const alt = this._escape(room.room_type_name || room.room_name || "Room")
    const src = this._escape(room.image_url).replace(/"/g, "%22")
    return `<div class="bw-room-image">
      <i class="bi bi-image" aria-hidden="true"></i>
      <img class="bw-room-image__img" src="${src}" alt="${alt}" loading="lazy"
           onerror="this.closest('.bw-room-image').classList.add('bw-room-image--placeholder'); this.remove();">
    </div>`
  }

  // ===== Modal picker (optional mode) =====
  //
  // Modal-specific instance vars are kept distinct from the inline picker's
  // so they don't clobber while both DOM subtrees exist:
  //   _accomCheckIn/_accomCheckOut — modal date-picker's range
  //   _selectedCard               — the modal-flow's clicked room card
  //   _lastAvailabilityRooms      — modal fetch result snapshot stash
  // Only on confirmRoom() does the modal flow write the shared
  // _checkIn/_checkOut/_selectedRoom canonical state via _commit().

  openModal(event) {
    event.preventDefault()
    if (!this.hasAccommodationModalTarget) return

    this._syncDiveDatesToAccomPicker()

    this.accommodationModalTarget.style.display = "flex"
    this.accommodationModalTarget.classList.remove("hidden")
  }

  // Push the current dive_dates hidden inputs into the accommodation date
  // picker so its highlighting + initial view-month reflect Step-1 edits.
  // Targets the MODAL picker specifically (not reachable via the outlet,
  // which is wired to the inline picker only).
  _syncDiveDatesToAccomPicker() {
    if (!this.hasAccomDatePickerTarget) return
    const pickerCtrl = this.application.getControllerForElementAndIdentifier(
      this.accomDatePickerTarget, "accom-date-picker"
    )
    if (pickerCtrl) {
      pickerCtrl.diveDatesValue = this._currentDiveDates()
    }
  }

  accomRangeChanged(event) {
    this._accomCheckIn = event.detail.checkIn
    this._accomCheckOut = event.detail.checkOut
  }

  closeModal(event) {
    if (event) event.preventDefault()
    if (!this.hasAccommodationModalTarget) return

    this.accommodationModalTarget.classList.add("hidden")
    this.accommodationModalTarget.style.display = "none"
  }

  async fetchAvailability(event) {
    event.preventDefault()
    const checkIn = this._accomCheckIn
    const checkOut = this._accomCheckOut

    if (!checkIn || !checkOut) {
      this._showModalError("Please select both check-in and check-out dates on the calendar.")
      return
    }

    if (checkOut <= checkIn) {
      this._showModalError("Check-out date must be after check-in date.")
      return
    }

    // Validate accommodation dates encompass all selected dive dates
    const diveDates = this._currentDiveDates()
    if (diveDates.length > 0) {
      const outsideDates = diveDates.filter(d => d < checkIn || d > checkOut)
      if (outsideDates.length > 0) {
        const formatted = outsideDates.map(d => {
          const dt = new Date(d + "T00:00:00")
          return dt.toLocaleDateString("en-US", { month: "short", day: "numeric" })
        }).join(", ")
        this._showModalError(`Your stay must include all dive dates. The following dates fall outside your stay: ${formatted}. Please adjust your check-in/check-out dates.`)
        return
      }
    }

    const loadingEl = this.element.querySelector("[data-accommodation-loading]")
    const resultsEl = this.element.querySelector("[data-accommodation-results]")
    if (loadingEl) loadingEl.classList.remove("hidden")
    if (resultsEl) resultsEl.innerHTML = ""

    try {
      // Send the current guest count so the server can bake green tax × adults
      // into the per-night rate. We also use `tax_config` to recompute
      // client-side when the user toggles guests without refetching.
      const adults = this._guests || 2
      const response = await fetch(`${this.availabilityUrlValue}?check_in=${checkIn}&check_out=${checkOut}&adults=${adults}`)
      const data = await response.json()

      if (loadingEl) loadingEl.classList.add("hidden")

      if (data.success && data.rooms && data.rooms.length > 0) {
        this._lastAvailabilityRooms = data.rooms
        this._taxConfig = data.tax_config || null
        this._renderModalRoomCards(data.rooms, checkIn, checkOut)
      } else {
        this._lastAvailabilityRooms = null
        this._taxConfig = null
        if (resultsEl) resultsEl.innerHTML = `<p class="text-sm text-gray-500 text-center py-4">No rooms available for these dates.</p>`
      }
    } catch (error) {
      console.error("Availability fetch failed:", error)
      if (loadingEl) loadingEl.classList.add("hidden")
      if (resultsEl) resultsEl.innerHTML = `<p class="text-sm text-red-500 text-center py-4">Unable to check availability. You can still submit your booking and we'll confirm manually.</p>`
    }
  }

  // Mirrors BusinessEntity#inclusive_total_cents — same cascade so client and
  // server agree on the inclusive total at every step of the modal flow.
  //   SC   = base × SC%
  //   TGST = (base + SC) × TGST%
  //   Green = green_per_pax_per_night × adults × nights
  //   Total = base*nights + SC*nights + TGST*nights + Green
  // The formula treats base as per-night (which matches what
  // EzeeService.merge_source_rates surfaces in `rate_ex_tax_per_night_cents`).
  _inclusiveCascadeCents(rateExTaxPerNightCents, adults, nights) {
    const cfg = this._taxConfig || {}
    const scPct = (cfg.service_charge_rate || 0) / 100
    const tgstPct = (cfg.tax_rate || 0) / 100
    const greenPerNight = (cfg.green_tax_per_adult_per_night_cents || 0) * (adults || 0)

    const scPerNight = Math.round(rateExTaxPerNightCents * scPct)
    const tgstPerNight = Math.round((rateExTaxPerNightCents + scPerNight) * tgstPct)
    const perNight = rateExTaxPerNightCents + scPerNight + tgstPerNight + greenPerNight

    return {
      perNightCents: perNight,
      scPerNightCents: scPerNight,
      tgstPerNightCents: tgstPerNight,
      greenPerNightCents: greenPerNight
    }
  }

  _formatMoney(cents) {
    return `$${(cents / 100).toFixed(2)}`
  }

  _renderModalRoomCards(rooms, checkIn, checkOut) {
    const resultsEl = this.element.querySelector("[data-accommodation-results]")
    if (!resultsEl) return

    const nights = this._nightsBetween(checkIn, checkOut)
    const selectedRoomTypeId = this._selectedCard?.roomTypeId || ""
    const guests = this._currentModalGuests()

    const html = rooms.map(room => {
      const available = parseInt(room.available) || 0
      const rateExTaxCents = parseInt(room.rate_ex_tax_per_night_cents, 10) || 0
      // Live-recompute the per-night inclusive rate for the current guest count
      // when we have an ex-tax base + tax_config from the server; otherwise
      // fall back to the server's per-night `rate` (already inclusive for the
      // adults the request was made with).
      const liveCents = (rateExTaxCents > 0 && this._taxConfig)
        ? this._inclusiveCascadeCents(rateExTaxCents, guests, nights).perNightCents
        : Math.round((parseFloat(room.rate) || 0) * 100)
      const rate = liveCents / 100
      const total = rate * nights
      const name = room.room_type_name || room.room_name || "Room"
      const roomTypeId = room.room_type_id || ""
      const maxAdults = parseInt(room.max_adults) || 2
      const soldOut = available <= 0
      const selectedAttr = (!soldOut && roomTypeId && roomTypeId === selectedRoomTypeId)
        ? "data-selected"
        : ""

      return `
        <div class="bw-room-card rounded ${soldOut ? "opacity-50" : ""}" style="border: 1px solid rgba(255,255,255,0.1); ${soldOut ? "" : "cursor: pointer;"} transition: all 0.2s ease;"
             ${soldOut ? "" : 'data-action="click->public-accommodation#selectModalRoom"'}
             ${selectedAttr}
             data-room-name="${this._escape(name)}"
             data-room-type-id="${roomTypeId}"
             data-rate-cents="${liveCents}"
             data-rate-ex-tax-cents="${rateExTaxCents}"
             data-nights="${nights}"
             data-check-in="${checkIn}"
             data-check-out="${checkOut}"
             data-max-adults="${maxAdults}"
             data-available="${available}">
          ${this._roomImageHtml(room)}
          <div class="p-3">
            <div class="d-flex justify-content-between align-items-start">
              <div>
                <div class="fw-semibold">${this._escape(name)}</div>
                <div class="small" style="color: rgba(255,255,255,0.5);">${soldOut ? "Sold out" : `${available} available`}</div>
              </div>
              ${soldOut ? `<span class="badge bg-danger">Sold out</span>` : `
                <div class="text-end">
                  <div class="fw-bold text-accent">$${rate.toFixed(2)}<span class="small fw-normal" style="color: rgba(255,255,255,0.4);">/night</span></div>
                  <div class="small" style="color: rgba(255,255,255,0.5);">${nights} nights = $${total.toFixed(2)}</div>
                </div>
              `}
            </div>
          </div>
        </div>`
    }).join("")

    resultsEl.innerHTML = `<div class="room-results-grid">${html}</div>`
  }

  // Reads the current guest count from the modal DOM. Falls back to the
  // canonical input, then the safe default of 2 (matches the server default
  // adults param in Public::AvailabilityController#clamp_adults).
  _currentModalGuests() {
    const fromDom = parseInt(this.element.querySelector("[data-accom-guests-count]")?.textContent, 10)
    if (fromDom > 0) return fromDom
    const fromInput = parseInt(this._input("guests_count")?.value, 10)
    return fromInput > 0 ? fromInput : 2
  }

  selectModalRoom(event) {
    const card = event.currentTarget
    this._selectedCard = {
      roomName: card.dataset.roomName,
      roomTypeId: card.dataset.roomTypeId,
      rateCents: parseInt(card.dataset.rateCents) || 0,
      rateExTaxCents: parseInt(card.dataset.rateExTaxCents, 10) || 0,
      nights: parseInt(card.dataset.nights) || 0,
      checkIn: card.dataset.checkIn,
      checkOut: card.dataset.checkOut,
      maxAdults: parseInt(card.dataset.maxAdults) || 2,
      available: parseInt(card.dataset.available) || 100
    }

    const resultsEl = this.element.querySelector("[data-accommodation-results]")
    if (resultsEl) this._markSelectedCard(resultsEl, card)

    this._showRoomDetailsSection()
  }

  // Sets `data-selected` on the chosen card and clears it from siblings.
  // The CSS rule keyed on [data-selected] applies the accent border; this
  // approach survives card re-renders (the room template re-emits the
  // attribute) so we don't lose the highlight after a fresh availability fetch.
  _markSelectedCard(scopeEl, card) {
    scopeEl.querySelectorAll("[data-room-name]").forEach(c => {
      if (c === card) c.setAttribute("data-selected", "")
      else c.removeAttribute("data-selected")
    })
  }

  _showRoomDetailsSection() {
    const card = this._selectedCard
    if (!card) return

    let existing = this.element.querySelector("[data-room-details-section]")

    if (!existing) {
      existing = document.createElement("div")
      existing.setAttribute("data-room-details-section", "")
      existing.className = "mt-4 p-3 rounded"
      existing.style.cssText = "border: 1px solid rgba(255,255,255,0.15); background: rgba(255,255,255,0.03);"

      const resultsEl = this.element.querySelector("[data-accommodation-results]")
      if (resultsEl) resultsEl.after(existing)
    }

    existing.innerHTML = `
      <h6 class="fw-bold text-uppercase small mb-3" style="color: var(--accent-color); letter-spacing: 0.05em;">Guests & Rooms</h6>

      <div class="d-flex align-items-center justify-content-between mb-3">
        <span class="small fw-medium" style="color: rgba(255,255,255,0.7);">Guests</span>
        <div class="d-flex align-items-center gap-2">
          <button type="button" class="btn btn-sm btn-outline-secondary rounded-3 d-flex align-items-center justify-content-center" style="width: 28px; height: 28px; border-color: rgba(255,255,255,0.2); color: rgba(255,255,255,0.6);"
                  data-action="click->public-accommodation#decrementGuests">&minus;</button>
          <span class="fw-bold text-white" style="min-width: 24px; text-align: center;" data-accom-guests-count>2</span>
          <button type="button" class="btn btn-sm btn-outline-secondary rounded-3 d-flex align-items-center justify-content-center" style="width: 28px; height: 28px; border-color: rgba(255,255,255,0.2); color: rgba(255,255,255,0.6);"
                  data-action="click->public-accommodation#incrementGuests">&plus;</button>
        </div>
      </div>

      <div class="d-flex align-items-center justify-content-between mb-3">
        <span class="small fw-medium" style="color: rgba(255,255,255,0.7);">Rooms</span>
        <div class="d-flex align-items-center gap-2">
          <button type="button" class="btn btn-sm btn-outline-secondary rounded-3 d-flex align-items-center justify-content-center" style="width: 28px; height: 28px; border-color: rgba(255,255,255,0.2); color: rgba(255,255,255,0.6);"
                  data-action="click->public-accommodation#decrementRooms">&minus;</button>
          <span class="fw-bold text-white" style="min-width: 24px; text-align: center;" data-accom-rooms-count>1</span>
          <button type="button" class="btn btn-sm btn-outline-secondary rounded-3 d-flex align-items-center justify-content-center" style="width: 28px; height: 28px; border-color: rgba(255,255,255,0.2); color: rgba(255,255,255,0.6);"
                  data-action="click->public-accommodation#incrementRooms">&plus;</button>
        </div>
      </div>

      <div class="text-center small mb-3" style="color: rgba(255,255,255,0.5);" data-accom-total-display></div>

      <button type="button" class="btn w-100 fw-bold py-2"
              style="background: var(--accent-color); color: #1a1a2e; border: none;"
              data-action="click->public-accommodation#confirmRoom">
        Confirm Room Selection
      </button>
    `

    this._updateAccomTotal()

    // Scroll the modal content so the Room Details section is visible
    const scrollParent = existing.closest("[style*='overflow']") || existing.closest(".modal-body") || existing.parentElement
    if (scrollParent) {
      const offset = existing.offsetTop - scrollParent.offsetTop
      scrollParent.scrollTo({ top: offset, behavior: "smooth" })
    }
  }

  // Render the breakdown the customer will see again on the confirmation
  // page. Source of truth for math is the selected card's ex-tax base and
  // the current guest count read from the DOM; if either is missing
  // (legacy/no tax_config), we degrade to the old nights × rooms × rate view.
  _updateAccomTotal() {
    const card = this._selectedCard
    if (!card) return

    const totalEl = this.element.querySelector("[data-accom-total-display]")
    if (!totalEl) return

    const rooms = parseInt(this.element.querySelector("[data-accom-rooms-count]")?.textContent) || 1
    const guests = this._currentModalGuests()
    const nights = card.nights || 0
    const canBreakDown = card.rateExTaxCents > 0 && this._taxConfig

    let perNightCents = card.rateCents
    let breakdownHtml = ""

    if (canBreakDown) {
      const bd = this._inclusiveCascadeCents(card.rateExTaxCents, guests, nights)
      perNightCents = bd.perNightCents
      // Update card.rateCents so confirmRoom + downstream invoice math see the
      // recomputed per-night rate for the actual guests, not the value baked
      // for whatever adults count the modal initially fetched with.
      card.rateCents = perNightCents
      breakdownHtml = this._modalBreakdownHtml(card.rateExTaxCents, bd, nights, rooms, guests)
    }

    const perNight = perNightCents / 100
    const total = perNight * nights * rooms

    totalEl.innerHTML =
      breakdownHtml +
      `<div class="text-center mt-2">` +
      `<span style="color: rgba(255,255,255,0.5);">${nights} night${nights > 1 ? "s" : ""} &times; ${rooms} room${rooms > 1 ? "s" : ""} &times; $${perNight.toFixed(2)}</span><br>` +
      `<span class="fw-bold" style="font-size: 1.1rem;">TOTAL: $${total.toFixed(2)}</span>` +
      `</div>`
  }

  _modalBreakdownHtml(rateExTaxCents, perNightBreakdown, nights, rooms, guests) {
    const baseTotal = rateExTaxCents * nights * rooms
    const scTotal = perNightBreakdown.scPerNightCents * nights * rooms
    const tgstTotal = perNightBreakdown.tgstPerNightCents * nights * rooms
    const greenTotal = perNightBreakdown.greenPerNightCents * nights * rooms
    const cfg = this._taxConfig || {}
    const scPct = (cfg.service_charge_rate || 0)
    const tgstPct = (cfg.tax_rate || 0)

    const row = (label, amountCents) =>
      `<div class="d-flex justify-content-between" style="color: rgba(255,255,255,0.7);">
         <span>${label}</span>
         <span>${this._formatMoney(amountCents)}</span>
       </div>`

    const rows = [row(`Room (ex-tax) &times; ${nights} night${nights > 1 ? "s" : ""}${rooms > 1 ? ` &times; ${rooms} rooms` : ""}`, baseTotal)]
    if (scTotal > 0) rows.push(row(`Service Charge (${scPct}%)`, scTotal))
    if (tgstTotal > 0) rows.push(row(`T-GST (${tgstPct}%)`, tgstTotal))
    if (greenTotal > 0) rows.push(row(`Green Tax (${guests} adult${guests > 1 ? "s" : ""} &times; ${nights} night${nights > 1 ? "s" : ""})`, greenTotal))

    return `<div class="small text-start mb-2" style="border-bottom: 1px solid rgba(255,255,255,0.1); padding-bottom: 8px;">${rows.join("")}</div>`
  }

  incrementGuests() {
    const el = this.element.querySelector("[data-accom-guests-count]")
    if (!el) return
    const val = parseInt(el.textContent) || 1
    const rooms = parseInt(this.element.querySelector("[data-accom-rooms-count]")?.textContent) || 1
    const maxPerRoom = this._selectedCard?.maxAdults || 10
    const max = maxPerRoom * rooms
    if (val < max) {
      el.textContent = val + 1
      this._afterGuestOrRoomChange()
    }
  }

  decrementGuests() {
    const el = this.element.querySelector("[data-accom-guests-count]")
    if (!el) return
    const val = parseInt(el.textContent) || 2
    if (val > 1) {
      el.textContent = val - 1
      this._afterGuestOrRoomChange()
    }
  }

  incrementRooms() {
    const el = this.element.querySelector("[data-accom-rooms-count]")
    if (!el) return
    const val = parseInt(el.textContent) || 1
    const max = this._selectedCard?.available || 10
    if (val < max) {
      el.textContent = val + 1
      this._afterGuestOrRoomChange()
    }
  }

  decrementRooms() {
    const el = this.element.querySelector("[data-accom-rooms-count]")
    if (!el) return
    const val = parseInt(el.textContent) || 1
    if (val > 1) {
      const newRooms = val - 1
      el.textContent = newRooms
      // Clamp guests to new max capacity
      const guestsEl = this.element.querySelector("[data-accom-guests-count]")
      if (guestsEl) {
        const maxPerRoom = this._selectedCard?.maxAdults || 10
        const maxGuests = maxPerRoom * newRooms
        const guests = parseInt(guestsEl.textContent) || 1
        if (guests > maxGuests) { guestsEl.textContent = maxGuests }
      }
      this._afterGuestOrRoomChange()
    }
  }

  // Single hook for both steppers: rerender the (visible) summary AND the
  // grid of available room cards. Without the grid refresh, the per-night
  // figures on the cards would lie when guests change — green tax × adults
  // is part of the per-night display, not just the total.
  _afterGuestOrRoomChange() {
    this._updateAccomTotal()
    if (this._lastAvailabilityRooms && this._selectedCard) {
      this._renderModalRoomCards(this._lastAvailabilityRooms, this._selectedCard.checkIn, this._selectedCard.checkOut)
    }
  }

  confirmRoom() {
    const card = this._selectedCard
    if (!card) return

    const guests = parseInt(this.element.querySelector("[data-accom-guests-count]")?.textContent) || 2
    const rooms = parseInt(this.element.querySelector("[data-accom-rooms-count]")?.textContent) || 1

    // Single canonical write — replaces the wizard's per-field hidden-input loop
    // and the separate _storeAvailabilitySnapshot call.
    this._commit({
      requires: true,
      checkIn: card.checkIn,
      checkOut: card.checkOut,
      roomType: card.roomName, // modal flow uses the room name as the room_type identifier
      pmsRoomTypeId: card.roomTypeId,
      guests,
      rooms,
      snapshot: this._buildModalSnapshotJson(card.checkIn, card.checkOut)
    })

    // Stash modal-flow rate so getInvoiceLines() and _renderSummary() can
    // price the row without re-fetching availability.
    this._modalRateCents = card.rateCents
    this._modalNights = card.nights
    this._guests = guests
    this._rooms = rooms

    // Carry the ex-tax base onto _selectedRoom so getTaxBreakdown's modal
    // branch can find it — previously only _selectedCard had it and that
    // field gets cleared the moment the modal closes, leaving the Payment
    // step's breakdown empty even though the inclusive line rendered fine.
    this._selectedRoom = {
      roomName: card.roomName,
      roomTypeId: card.roomTypeId,
      rateExTaxCents: card.rateExTaxCents
    }

    this.render()
    this.closeModal()

    // Broadcast so the wizard's Review and Payment surfaces refresh.
    // public-dives:datesChanged is wired the same way for dive selections;
    // accommodation needs the same channel or the Review total goes stale
    // when accommodation is added/removed after the user already entered
    // the Review step.
    this.dispatch("changed", { bubbles: true, detail: { added: true } })
  }

  removeAccommodation(event) {
    if (event) event.preventDefault()
    this._clearAccommodation()
    this._modalRateCents = null
    this._modalNights = null
    this._selectedRoom = null
    this.render()
    this.dispatch("changed", { bubbles: true, detail: { added: false } })
  }

  _buildModalSnapshotJson(checkIn, checkOut) {
    return this._snapshotJson(this._lastAvailabilityRooms, checkIn, checkOut, false)
  }

  _showModalError(message) {
    const resultsEl = this.element.querySelector("[data-accommodation-results]")
    if (resultsEl) {
      resultsEl.innerHTML = `<p class="text-sm text-red-500 text-center py-2">${message}</p>`
    }
  }

  // ===== Render =====
  //
  // Single entry point that reconciles DOM visibility + summary contents
  // with the current state machine. Called from connect(), the cross-
  // controller event handlers, and the inline/modal write paths.

  render() {
    if (this.modeValue === "off") {
      this.sectionTarget.classList.add("hidden")
      return
    }
    this.sectionTarget.classList.remove("hidden")

    if (this.modeValue === "bundled") {
      this._showInlineBundled()
    } else {
      this._showOptionalModal()
    }
    this._renderSummary()
  }

  _showInlineBundled() {
    if (this.hasInlinePickerTarget) this.inlinePickerTarget.classList.remove("hidden")
    if (this.hasModalTriggerTarget) this.modalTriggerTarget.classList.add("hidden")
  }

  _showOptionalModal() {
    if (this.hasInlinePickerTarget) this.inlinePickerTarget.classList.add("hidden")
    if (this.hasModalTriggerTarget) this.modalTriggerTarget.classList.remove("hidden")
  }

  _renderSummary() {
    if (!this.hasSummaryTarget) return

    // Stale prompt takes priority — it's the customer's call to action.
    if (this._stalePromptRange) {
      const { earliest, latest } = this._stalePromptRange
      this.summaryTarget.innerHTML = stalePromptHtml(earliest, latest, this.localesValue)
      return
    }

    if (this._requiresAccommodation() && this._roomPicked()) {
      this.summaryTarget.innerHTML = selectedSummaryHtml(this._summaryState(), this.localesValue)
      return
    }

    this.summaryTarget.innerHTML = ""
  }

  _summaryState() {
    return {
      roomType: this._roomType,
      checkIn: this._checkIn,
      checkOut: this._checkOut,
      rooms: this._rooms || 1,
      nights: this._modalNights || this.getNights(),
      // rateCents is null after a server re-render rehydration (only canonical
      // inputs persist); templates handle null by skipping the dollar amount.
      rateCents: this._modalRateCents
    }
  }

  _nightsBetween(checkIn, checkOut) {
    if (!checkIn || !checkOut) return 0
    return Math.round((new Date(checkOut) - new Date(checkIn)) / 86400000)
  }

  // Action handler for the stale-prompt "Re-pick" CTA. Pushes the prefill
  // range into whichever picker is active for the current mode.
  openPickerPrefilled(event) {
    event.preventDefault()
    const checkIn = event.currentTarget.dataset.prefillCheckIn
    const checkOut = event.currentTarget.dataset.prefillCheckOut

    if (this.modeValue === "bundled") {
      if (checkIn && checkOut && this.hasAccomDatePickerOutlet) {
        const picker = this.accomDatePickerOutlet
        picker.checkIn = checkIn
        picker.checkOut = checkOut
        picker.renderCalendar()
      }
    } else {
      this._accomCheckIn = checkIn
      this._accomCheckOut = checkOut
      this.openModal(event)
    }
  }
}
